import { ChronoUnit, TemporalAdjusters } from "@js-joda/core";
import { DateUtil } from "../utils/DateUtil";
/**
 * Day-count conventions supported by LendPeak.
 *
 * ─ ACTUAL types use the real day gap in the numerator.
 * ─ 30/360  (European) adjusts both dates (31 → 30).
 * ─ 30/360U (US/NASD) has an extra rule for the end-day.
 */
export var CalendarType;
(function (CalendarType) {
    CalendarType[CalendarType["ACTUAL_ACTUAL"] = 0] = "ACTUAL_ACTUAL";
    CalendarType[CalendarType["ACTUAL_360"] = 1] = "ACTUAL_360";
    CalendarType[CalendarType["ACTUAL_365"] = 2] = "ACTUAL_365";
    CalendarType[CalendarType["ACTUAL_365_NL"] = 3] = "ACTUAL_365_NL";
    CalendarType[CalendarType["THIRTY_360"] = 4] = "THIRTY_360";
    CalendarType[CalendarType["THIRTY_ACTUAL"] = 5] = "THIRTY_ACTUAL";
    CalendarType[CalendarType["THIRTY_360_US"] = 6] = "THIRTY_360_US";
})(CalendarType || (CalendarType = {}));
export class Calendar {
    calendarType;
    /* ────────────────────────────────────────────────────────── */
    /*  ctor & basic accessors                                    */
    /* ────────────────────────────────────────────────────────── */
    _date;
    constructor(calendarType = CalendarType.ACTUAL_ACTUAL, date = DateUtil.today()) {
        this.calendarType = calendarType;
        this.date = date;
    }
    set date(d) {
        this._date = DateUtil.normalizeDate(d);
    }
    get date() {
        return this._date;
    }
    /* ────────────────────────────────────────────────────────── */
    /*  Human-readable label                                      */
    /* ────────────────────────────────────────────────────────── */
    get userFriendlyName() {
        switch (this.calendarType) {
            case CalendarType.ACTUAL_ACTUAL:
                return "Actual/Actual";
            case CalendarType.ACTUAL_360:
                return "Actual/360";
            case CalendarType.ACTUAL_365:
                return "Actual/365 (Fixed)";
            case CalendarType.ACTUAL_365_NL:
                return "Actual/365 (No-Leap)";
            case CalendarType.THIRTY_360:
                return "30/360 (European)";
            case CalendarType.THIRTY_ACTUAL:
                return "30/Actual";
            case CalendarType.THIRTY_360_US:
                return "30/360 (U.S.)";
            default:
                return "Unknown";
        }
    }
    /* ────────────────────────────────────────────────────────── */
    /*  Public API                                                */
    /* ────────────────────────────────────────────────────────── */
    /** Signed day-count between two dates. */
    daysBetween(d1, d2) {
        switch (this.calendarType) {
            case CalendarType.THIRTY_360:
                return this.daysBetween30_360_EU(d1, d2);
            case CalendarType.THIRTY_360_US:
                return this.daysBetween30_360_US(d1, d2);
            case CalendarType.THIRTY_ACTUAL:
                return this.daysBetween30_Actual(d1, d2);
            case CalendarType.ACTUAL_365_NL:
                return this.daysBetweenActual365NoLeap(d1, d2);
            case CalendarType.ACTUAL_360:
            /* fall-through */
            case CalendarType.ACTUAL_365:
            /* fall-through */
            case CalendarType.ACTUAL_ACTUAL:
                return ChronoUnit.DAYS.between(d1, d2);
            default:
                throw new Error("Invalid calendar type");
        }
    }
    /** Whole-month difference (signed). */
    monthsBetween(d1, d2) {
        return ChronoUnit.MONTHS.between(d1, d2);
    }
    /** Add _n_ months, preserving “end-of-month” intent. */
    addMonths(date, months) {
        const cand = date.plusMonths(months);
        const lastSrc = date.with(TemporalAdjusters.lastDayOfMonth());
        const lastTgt = cand.with(TemporalAdjusters.lastDayOfMonth());
        return date.dayOfMonth() === lastSrc.dayOfMonth() ? lastTgt : cand;
    }
    /** Days in month for the current convention. */
    daysInMonth(date) {
        switch (this.calendarType) {
            case CalendarType.THIRTY_360:
            case CalendarType.THIRTY_360_US:
            case CalendarType.THIRTY_ACTUAL:
                return 30;
            default:
                return date.lengthOfMonth();
        }
    }
    /** Days in year for the current convention. */
    daysInYear(date = this.date) {
        switch (this.calendarType) {
            case CalendarType.THIRTY_360:
            case CalendarType.THIRTY_360_US:
            case CalendarType.ACTUAL_360:
                return 360;
            case CalendarType.ACTUAL_365:
            /* fall-through */
            case CalendarType.ACTUAL_365_NL:
                return 365;
            case CalendarType.THIRTY_ACTUAL:
                return 360;
            case CalendarType.ACTUAL_ACTUAL:
                return date.isLeapYear() ? 366 : 365;
            default:
                throw new Error("Invalid calendar type");
        }
    }
    /** Inclusive start, exclusive end. */
    isDateBetween(d, start, end) {
        return !d.isBefore(start) && d.isBefore(end);
    }
    /* ────────────────────────────────────────────────────────── */
    /*  Private helpers                                           */
    /* ────────────────────────────────────────────────────────── */
    /** 30E/360 (European): 31 ▶ 30 on **both** dates. */
    daysBetween30_360_EU(s, e) {
        let [d1, d2] = [s.dayOfMonth(), e.dayOfMonth()].map((d) => (d === 31 ? 30 : d));
        return 360 * (e.year() - s.year()) + 30 * (e.monthValue() - s.monthValue()) + (d2 - d1);
    }
    /** 30U/360 (NASD). */
    daysBetween30_360_US(s, e) {
        let d1 = s.dayOfMonth();
        let d2 = e.dayOfMonth();
        if (d1 === 31)
            d1 = 30;
        if (d2 === 31 && d1 >= 30)
            d2 = 30;
        return 360 * (e.year() - s.year()) + 30 * (e.monthValue() - s.monthValue()) + (d2 - d1);
    }
    /** 30/Actual –
     *  Same-month  ⇒  actual days
     *  Cross-month ⇒  30E/360 rule
     */
    daysBetween30_Actual(s, e) {
        if (s.year() === e.year() && s.monthValue() === e.monthValue()) {
            return ChronoUnit.DAYS.between(s, e); // stay inside the month
        }
        return this.daysBetween30_360_EU(s, e); // cross-month ⇒ 30E/360
    }
    /** ACT/365 No-Leap – skips 29 Feb. */
    daysBetweenActual365NoLeap(s, e) {
        const sign = s.isAfter(e) ? -1 : 1;
        let start = sign === 1 ? s : e;
        const end = sign === 1 ? e : s;
        let days = 0;
        while (start.isBefore(end)) {
            if (!(start.monthValue() === 2 && start.dayOfMonth() === 29)) {
                days++;
            }
            start = start.plusDays(1);
        }
        return sign * days;
    }
}
